import { useEffect, useRef, useState } from "react";

import { CONNECTION_STATUS } from "../_constants/connectionStatus";

interface Statuses {
  ONLINE: "online";
  OFFLINE: "offline";
  RECOVERING: "recovering";
  RECOVERED: "recovered";
}

export interface UseOnlineReturn {
  status: Statuses[keyof Statuses];
}

export const useOnline = (): UseOnlineReturn => {
  const initialStatus = navigator.onLine
    ? CONNECTION_STATUS.ONLINE
    : CONNECTION_STATUS.OFFLINE;
  const [status, setStatus] = useState<Statuses[keyof Statuses]>(initialStatus);
  const recoveringTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const recoveredTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    const scheduleRecoveryTransition = () => {
      recoveringTimeoutRef.current = setTimeout(
        () => setStatus(CONNECTION_STATUS.RECOVERED),
        1000,
      );
      recoveredTimeoutRef.current = setTimeout(
        () => setStatus(CONNECTION_STATUS.ONLINE),
        6000,
      );
    };

    const handleOnline = () =>
      setStatus((prev) => {
        clearTimeout(recoveringTimeoutRef.current);
        clearTimeout(recoveredTimeoutRef.current);

        if (prev === CONNECTION_STATUS.OFFLINE) {
          scheduleRecoveryTransition();
          return CONNECTION_STATUS.RECOVERING;
        }
        return CONNECTION_STATUS.ONLINE;
      });

    const handleOffline = () => {
      clearTimeout(recoveringTimeoutRef.current);
      clearTimeout(recoveredTimeoutRef.current);
      setStatus("offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearTimeout(recoveringTimeoutRef.current);
      clearTimeout(recoveredTimeoutRef.current);
    };
  }, []);

  return { status };
};
